import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Instagram, MapPin, ArrowRight } from "lucide-react";
import { CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = ["Catering", "Private Chef", "Meal Prep", "Other"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    event_date: "",
    guest_count: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", event_date: "", guest_count: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-12">
        <div className="eyebrow mb-6">Contact</div>
        <h1 className="font-serif-display text-5xl lg:text-7xl max-w-4xl leading-[1.04]" style={{ color: "var(--ooe-ink)" }}>
          Let's begin — tell us about your table.
        </h1>
        <p className="mt-8 max-w-2xl text-[16px] leading-relaxed" style={{ color: "var(--ooe-ink-2)" }}>
          Whether it's a corporate lunch, a backyard gathering, an intimate at-home tasting menu, or a week of chef-prepped meals — share a few
          details and we'll respond within one business day.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-32">
        <div className="grid lg:grid-cols-12 gap-0" style={{ border: "1px solid var(--ooe-border)" }}>
          {/* Form */}
          <div className="lg:col-span-7 p-8 lg:p-14">
            {submitted ? (
              <div className="py-16 text-center" data-testid="contact-success">
                <div className="eyebrow mb-4">Received</div>
                <h2 className="font-serif-display text-4xl lg:text-5xl" style={{ color: "var(--ooe-brand)" }}>Thank you.</h2>
                <p className="mt-6 max-w-md mx-auto text-[15px]" style={{ color: "var(--ooe-ink-2)" }}>
                  We've received your inquiry and will reach out shortly to begin shaping your experience.
                </p>
                <button onClick={() => setSubmitted(false)} className="btn-ghost mt-10" data-testid="contact-new-inquiry">
                  Send another inquiry <ArrowRight size={14} />
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-7" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label className="label-line">Name *</label>
                    <input className="input-line" name="name" value={form.name} onChange={onChange} required data-testid="input-name" />
                  </div>
                  <div>
                    <label className="label-line">Email *</label>
                    <input className="input-line" name="email" type="email" value={form.email} onChange={onChange} required data-testid="input-email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label className="label-line">Phone</label>
                    <input className="input-line" name="phone" value={form.phone} onChange={onChange} data-testid="input-phone" />
                  </div>
                  <div>
                    <label className="label-line">Service</label>
                    <select className="input-line" name="service" value={form.service} onChange={onChange} data-testid="input-service">
                      <option value="">Select a service…</option>
                      {services.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label className="label-line">Event Date</label>
                    <input className="input-line" name="event_date" type="date" value={form.event_date} onChange={onChange} data-testid="input-event-date" />
                  </div>
                  <div>
                    <label className="label-line">Guest Count</label>
                    <input className="input-line" name="guest_count" value={form.guest_count} onChange={onChange} placeholder="e.g. 12" data-testid="input-guest-count" />
                  </div>
                </div>
                <div>
                  <label className="label-line">Tell us about your event *</label>
                  <textarea className="input-line" rows={5} name="message" value={form.message} onChange={onChange} required data-testid="input-message" />
                </div>
                <button type="submit" disabled={submitting} className="btn-primary" data-testid="contact-submit-btn">
                  {submitting ? "Sending…" : "Send Inquiry"} <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>

          {/* Right info panel */}
          <aside className="lg:col-span-5 p-8 lg:p-14" style={{ background: "var(--ooe-dark)", color: "var(--ooe-on-dark)" }}>
            <div className="eyebrow mb-6" style={{ color: "rgba(249,248,246,0.7)" }}>Reach Us Directly</div>
            <h3 className="font-serif-display text-3xl lg:text-4xl">Out of Eden</h3>
            <p className="mt-3 text-[14px]" style={{ color: "rgba(249,248,246,0.7)" }}>
              Based in {CONTACT.location} — serving the surrounding region.
            </p>

            <ul className="mt-10 space-y-6 text-[15px]">
              <li className="flex items-center gap-4" data-testid="contact-info-email">
                <Mail size={18} />
                <a href={`mailto:${CONTACT.email}`} className="hover:underline">{CONTACT.email}</a>
              </li>
              <li className="flex items-center gap-4" data-testid="contact-info-phone">
                <Phone size={18} />
                <a href={`tel:${CONTACT.phone.replace(/-/g, "")}`} className="hover:underline">{CONTACT.phone}</a>
              </li>
              <li className="flex items-center gap-4" data-testid="contact-info-instagram">
                <Instagram size={18} />
                <a href={CONTACT.instagramUrl} target="_blank" rel="noreferrer" className="hover:underline">{CONTACT.instagram}</a>
              </li>
              <li className="flex items-center gap-4" data-testid="contact-info-location">
                <MapPin size={18} />
                <span>{CONTACT.location}, Canada</span>
              </li>
            </ul>

            <div className="mt-12 pt-8" style={{ borderTop: "1px solid rgba(249,248,246,0.15)" }}>
              <div className="eyebrow mb-2" style={{ color: "rgba(249,248,246,0.55)" }}>Response time</div>
              <p className="text-[14px]" style={{ color: "rgba(249,248,246,0.78)" }}>
                We respond to inquiries within one business day. For urgent
                requests, please call directly.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
